import appStore from '@stores/appStore'

/**
 * Tenant setting keys (must mirror the backend constants in
 * `AppSettings.Attachment.OffshoreBlockEnabled` /
 * `AppSettings.Attachment.OffshoreBlockAll` /
 * `AppSettings.Attachment.OffshoreBlockedAttachmentTypes`).
 */
const SETTING_ENABLED = 'ICS.Attachment.OffshoreBlockEnabled'
const SETTING_BLOCK_ALL = 'ICS.Attachment.OffshoreBlockAll'
const SETTING_BLOCKED_TYPES = 'ICS.Attachment.OffshoreBlockedAttachmentTypes'

/** Reads a "True"/"False" tenant setting off the current tenant. */
function isSettingOn(key: string): boolean {
  return (
    (appStore.currentTenant?.settings?.[key] || '').toLowerCase() === 'true'
  )
}

/**
 * Parses the CSV stored in the tenant setting into a Set of enum-name strings
 * (e.g. "COMPLETED_REPORT,DICTATION_REPORT" -> {"COMPLETED_REPORT", "DICTATION_REPORT"}).
 * Trims entries, drops empties; case-sensitive (backend stores canonical enum spelling).
 */
function getBlockedTypeSet(): Set<string> {
  const csv = appStore.currentTenant?.settings?.[SETTING_BLOCKED_TYPES] || ''
  if (!csv) {
    return new Set()
  }

  return new Set(
    csv
      .split(',')
      .map(s => (s || '').trim())
      .filter(Boolean)
  )
}

/**
 * Returns `true` when the current user is allowed to READ THE CONTENT of an
 * attachment of the given `attachmentType` (an `AttachmentTypeEnum` name string,
 * e.g. `'PAPERWORK'`, `'COMPLETED_REPORT'`) — that is: open it in the PDF viewer,
 * download it, or ask for its AI summary.
 *
 * It says nothing about MANAGING the file. Offshore staff upload, delete, cleanup
 * and rename blocked attachments exactly like onshore staff do; the policy only
 * withholds the content. Do not reuse this to gate an upload or delete control.
 * (Two exceptions the backend also enforces: re-typing a blocked attachment and
 * merging blocked source documents are refused, because both would launder the
 * content into a downloadable type.)
 *
 * Returns `true` in any of these cases (i.e. NO restriction applies):
 * - The tenant feature `OffshoreStaffAccessControl` is off
 * - The tenant setting toggle `OffshoreBlockEnabled` is off
 * - The current user is NOT an offshore staff
 * - `attachmentType` is empty / null / unknown — defensive default, unless the
 *   tenant is in blanket mode (see below)
 * - The type is not in the tenant block list
 *
 * Returns `false` when ALL of: feature on + setting on + user is offshore
 * staff + type is in the block list — or, regardless of type, when the tenant
 * turned on `OffshoreBlockAll`. Blanket mode is checked BEFORE the empty-type
 * default on purpose: "block every attachment" has to cover the rows a surface
 * renders without a type as well, which is exactly what the backend does when
 * an id does not resolve in the lookup table.
 *
 * The backend enforces the same rule (see `AttachmentAccessChecker`); the FE
 * gating is purely UX — it disables buttons / hides controls so the user does
 * not hit a 403 mid-flow.
 */
export function canAccessAttachmentType(
  attachmentType: string | null | undefined
): boolean {
  if (!appStore.ifOffshoreStaff()) {
    return true
  }

  if (!isSettingOn(SETTING_ENABLED)) {
    return true
  }

  if (isSettingOn(SETTING_BLOCK_ALL)) {
    return false
  }

  if (!attachmentType) {
    return true
  }

  const blocked = getBlockedTypeSet()
  if (blocked.size === 0) {
    return true
  }

  return !blocked.has(attachmentType)
}

/**
 * Inverse of {@link canAccessAttachmentType} — handy when hiding the content
 * buttons (Summary / View PDF / Download) on a file row. Same caveat: this is
 * not a "can the user touch this attachment" flag.
 */
export function isAttachmentTypeBlocked(
  attachmentType: string | null | undefined
): boolean {
  return !canAccessAttachmentType(attachmentType)
}

/**
 * Blanket restriction check — true when the current user is an offshore staff
 * AND the tenant has the Attachment Access Control feature enabled, regardless
 * of any specific attachment type / block list entry.
 *
 * Used by surfaces that show free-form attachment-like data with no
 * `attachmentType` to compare against (e.g. paperwork share links). Per
 * product decision, those surfaces are restricted whenever the global toggle
 * is on for an offshore staff — no per-type granularity.
 *
 * Backend equivalent: feature flag + `OffshoreBlockEnabled` setting only,
 * without consulting the `OffshoreBlockedAttachmentTypes` list.
 */
export function isAttachmentAccessGloballyRestricted(): boolean {
  // ifOffshoreStaff() is the single source of truth for who the offshore rules
  // apply to (STAFF or ADMIN_STAFF + flag + feature) — no extra role pre-filter.
  if (!appStore.ifOffshoreStaff()) {
    return false
  }

  return isSettingOn(SETTING_ENABLED)
}

/**
 * True when the tenant chose the blanket "Block all attachments" mode for
 * offshore staff — the master toggle is on AND `OffshoreBlockAll` is on. The
 * per-type block list is ignored in that mode.
 *
 * Exposed mainly for surfaces that want to word their empty state ("all
 * documents are restricted") differently from a per-type block.
 */
export function isAllAttachmentsBlockedForOffshoreStaff(): boolean {
  if (!appStore.ifOffshoreStaff()) {
    return false
  }

  return isSettingOn(SETTING_ENABLED) && isSettingOn(SETTING_BLOCK_ALL)
}

/**
 * Restriction check for "Share Download Link" / "Share Password" field pairs
 * (dictation files, final report, MR files, med-neg LOI links, LEAP views).
 *
 * Rides the Attachment Access Control toggle (`OffshoreBlockEnabled`), the same
 * switch as {@link isAttachmentAccessGloballyRestricted}: a share link is a way
 * to download attachments, so it is withheld only when the tenant turned that
 * policy on. The per-type block list is not consulted — a link has no type.
 */
export function isShareLinkRestrictedForOffshoreStaff(): boolean {
  return isAttachmentAccessGloballyRestricted()
}

/** Human-readable message used in tooltips when an action is disabled. */
export const ATTACHMENT_ACCESS_DENIED_MESSAGE =
  'Access to this attachment type is restricted by tenant Attachment Access Control policy.'
